import React from 'react';
import { ShoppingCart, Heart, Check } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Badge } from './Badge';
import { SoldOutRibbon } from './SoldOutRibbon';
import { ProductStamp } from './ProductStamp';
import IntensityIndicator from '../IntensityIndicator';

// Labels for matched criteria fields
const CRITERIA_LABELS = {
  olfactory_family: 'Familia',
  mood: 'Ocasión',
  intensity: 'Intensidad',
  popularity: 'Popularidad'
};

export const ProductCard = ({
  image,
  productCode,
  name,
  brand,
  price,
  originalPrice,
  badge,
  gender,
  intensity,
  stock = 1,
  matchPercentage,
  matchedCriteria = [],
  onAddToCart,
  onAddToWishlist,
  href,
  className,
}) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const [isFavorite, setIsFavorite] = React.useState(false);
  
  const isOutOfStock = stock === 0;

  // Determine name color based on gender
  const getNameColorClass = () => {
    if (gender === 'Hombre') return 'text-brand-cobalt hover:text-brand-cobalt/80';
    if (gender === 'Mujer') return 'text-brand-magenta hover:text-brand-magenta/80';
    return 'text-brand-magenta hover:text-brand-magenta/80'; // Unisex defaults a magenta
  };

  // Determine hover glow variant based on gender (ver .brand-card.hybrid-glow en index.css)
  const getHoverShadowClass = () => {
    if (gender === 'Mujer') return 'magenta-accent';
    return ''; // Default cobalt manejado por la clase base
  };

  // Determine quick-add button styling based on gender
  const getAddToCartClass = () => {
    if (gender === 'Mujer') return 'bg-brand-magenta hover:bg-brand-magenta/90';
    return 'bg-brand-cobalt hover:bg-brand-cobalt/90';
  };

  const handleAddToCart = (e) => {
    e.preventDefault();
    if (isOutOfStock) return;
    onAddToCart?.();
  };

  const handleToggleFavorite = (e) => {
    e.preventDefault();
    setIsFavorite(!isFavorite);
    onAddToWishlist?.();
  };

  return (
    <div
      className={cn(
        "group relative animate-fade-in-up brand-card hybrid-glow rounded-lg p-2",
        getHoverShadowClass(),
        className
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid="product-card"
    >
      {/* Image Container - Fixed aspect ratio */}
      <a href={href} className="block relative aspect-square overflow-hidden mb-2 rounded bg-brand-black/5">
        <img
          src={image}
          alt={name}
          className={cn(
            "w-full h-full object-cover transition-transform duration-600 ease-luxury group-hover:scale-105",
            isOutOfStock && "opacity-60"
          )}
          loading="lazy"
        />

        {/* Sello Alphatica - antes del botón "Agregar" para que éste lo cubra en hover */}
        <ProductStamp size="catalog" />

        {/* Cinta diagonal "Agotado" - tiene prioridad sobre otros badges */}
        {isOutOfStock ? (
          <SoldOutRibbon />
        ) : badge && (
          <div className="absolute top-3 left-3 z-10">
            {/* En productos de Mujer, el badge sigue el mismo acento magenta que el resto de la tarjeta */}
            <Badge variant={badge.variant} className={gender === 'Mujer' ? 'bg-brand-magenta' : ''}>{badge.label}</Badge>
          </div>
        )}

        {/* Wishlist Button */}
        <button
          onClick={handleToggleFavorite}
          className={cn(
            "absolute top-3 right-3 z-10 w-8 h-8 bg-white/80 backdrop-blur-sm border border-brand-black/15 flex items-center justify-center transition-all duration-300 rounded opacity-0 group-hover:opacity-100",
            isFavorite && "opacity-100"
          )}
          aria-label="Add to wishlist"
        >
          <Heart
            size={14}
            className={cn(
              "transition-colors",
              isFavorite ? "fill-brand-magenta text-brand-magenta" : "text-brand-black/60"
            )}
          />
        </button>

        {/* Quick Add Button */}
        {!isOutOfStock && (
          <div
            className={cn(
              "absolute bottom-0 left-0 right-0 transition-all duration-300 transform",
              isHovered ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"
            )}
          >
            <button
              onClick={handleAddToCart}
              className={cn(
                "w-full text-white py-2.5 px-3 flex items-center justify-center gap-2 transition-colors text-xs uppercase tracking-wider font-medium",
                getAddToCartClass()
              )}
              data-testid="add-to-cart-btn"
            >
              <ShoppingCart size={14} />
              <span>Agregar</span>
            </button>
          </div>
        )}
      </a>

      {/* Product Info - Fixed height container */}
      <div className="text-center flex flex-col" style={{ minHeight: '140px' }}>
        <div>
          {/* Código de producto - negritas */}
          {productCode && (
            <p className="text-sm font-bold text-brand-black truncate">
              {productCode}
            </p>
          )}
          {/* Inspirado en: - pequeño */}
          <p className="text-[9px] text-brand-black/50">Inspirado en:</p>
          {/* Nombre - Color según género - UNA sola línea con truncate */}
          <a
            href={href}
            className={cn("block text-sm font-bold transition-colors truncate", getNameColorClass())}
            title={name}
          >
            {name}
          </a>
          {/* Marca */}
          {brand && (
            <p className="text-xs text-brand-black/70 truncate">
              {brand}
            </p>
          )}

          {/* Matched Criteria Checkmarks */}
          {matchedCriteria && matchedCriteria.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {matchedCriteria.slice(0, 3).map((criteria, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-green-500/10 border border-green-500/30 rounded text-[9px] text-green-700"
                >
                  <Check size={8} className="flex-shrink-0" />
                  <span className="truncate max-w-[60px]">
                    {typeof criteria.value === 'string' ? criteria.value : CRITERIA_LABELS[criteria.field] || criteria.field}
                  </span>
                </span>
              ))}
            </div>
          )}
        </div>
        
        {/* Bottom section - Price and Intensity - Always at bottom */}
        <div className="mt-auto pt-2">
          {/* Precio */}
          <div className="flex items-center justify-center gap-1">
            <span className="text-sm font-semibold text-brand-black">
              ${price.toLocaleString('es-MX')} MXN
            </span>
            {originalPrice && (
              <span className="text-xs text-brand-black/40 line-through">
                ${originalPrice.toLocaleString('es-MX')}
              </span>
            )}
          </div>
          
          {/* Intensity Indicator - Fixed height */}
          <div className="h-6">
            {intensity && (
              <IntensityIndicator intensity={intensity} size="sm" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

ProductCard.displayName = "ProductCard";
